import React, { useState, useEffect, forwardRef } from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeaderStepper } from '../Common/HeaderStepper/HeaderStepper';
import SuppData from '../../Helpers/Data/Products/Supplemental/SuppData';
import * as Steps from './steps';
import { saveSessionEvent } from "../../../session";
import { getSessionStorageItem, setSessionStorageItem, getBasicInfo, setBasicInfo } from '../../../sessionStorage';
import { JourneyTypes } from '../../Helpers/Constants/JourneyTypes';

const Supplemental = forwardRef((props, ref) => {

  const suppData = SuppData(props);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent() }, [location])

  const SESSION_STORAGE_KEY = "COVR_SUPPLEMENTAL";
  var sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_KEY);
  const [triggerNext, setTriggerNext] = useState(false);

  if (props.landing && sessionStorageFormState) {
    sessionStorageFormState.currentStep = 0;
  }

  const path = location ? location.pathname.split("/").pop() : null;
  const prefill = location && location.search && location.search.toLowerCase().includes('prefill');

  const basicInfo = getBasicInfo();

  const newFormState = {
    currentStep: 0,
    pages: {
      basicInfo: Steps.BasicInfo.DefaultFormState(prefill, basicInfo),
      quoter: Steps.Quoter.DefaultFormState(prefill),
      appointment: Steps.Appointment.DefaultFormState(prefill, basicInfo)
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  const [formState, setFormState] = useState(initialFormState);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path || path === steps[formState.currentStep].path) {
      return;
    }

    const nextStep = steps.findIndex(s => s.path === path);

    if (nextStep < 0) {
      return;
    }

    const newFormState = { ...formState, currentStep: nextStep };
    setFormState(newFormState);
  }, [path]);

  useEffect(() => {
    if (triggerNext && !props.emailCaptureActive) {
      onNext();
    }
  }, [triggerNext, props.emailCaptureActive]);

  const onNext = () => {
    if (formState.currentStep >= steps.length - 1) {
      console.error("No next step!");
      return;
    }
    if (formState.currentStep === 0) {
      const basicInfo = getBasicInfo();
      const basicInfoToStore = formState.pages.basicInfo;
      basicInfoToStore.firstName = basicInfo.firstName || '';
      basicInfoToStore.lastName = basicInfo.lastName || '';
      setBasicInfo({...basicInfo, ...basicInfoToStore});
    }

    const nextStepPath = steps[formState.currentStep + 1].path;
    const newFormState = { ...formState, currentStep: formState.currentStep + 1 };

    setFormState(newFormState);
    setSessionStorageItem(SESSION_STORAGE_KEY, newFormState);

    navigate(`/quote/supplemental/${nextStepPath}`);
    window.scroll(0, 0);
  };

  const onBack = () => {
    if (formState.currentStep <= 1) {
      navigate('landing/supplemental');
      return;
    }

    const backStepPath = steps[formState.currentStep - 1].path;
    const newFormState = { ...formState, currentStep: formState.currentStep - 1 };

    setFormState(newFormState);
    setSessionStorageItem(SESSION_STORAGE_KEY, newFormState);

    navigate(`/quote/supplemental/${backStepPath}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const steps = [
    {
      path: 'basic-info',
      progressTitle: 'Basic Information',
      component: (
        <Steps.BasicInfo
          formState={formState.pages.basicInfo}
          onFieldChange={(field, value) => onFieldChange('basicInfo', field, value)}
          onNext={() => {
            setTriggerNext(true);
          }}
          onBack={onBack}
          hideBack={props.landing}
          ageSpecs={suppData.specs.age}
          ref={ref}
          setModalSaveQuoteIsOpen={props.setModalSaveQuoteIsOpen}
          setSaveQuoteData={props.setSaveQuoteData}
          saveQuoteContact={props.saveQuoteContact}
          emailCapture={props.emailCapture}
          setEmailCaptureActive={props.setEmailCaptureActive}
        />
      ),
    },
    {
      path: 'get-a-quote',
      progressTitle: 'Get a Quote',
      headerText: 'Your Quote Results',
      component: (
        <Steps.Quoter
          formState={formState.pages.quoter}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('quoter', field, value)}
          onBasicInfoFieldChange={(field, value) => onFieldChange('basicInfo', field, value)}
          onNext={onNext}
          onBack={onBack}
          ageSpecs={suppData.specs.age}
          onArrayChange={(fs) => {
            setFormState(fs);
          }}
          fullFormState={formState}
        />
      ),
      hideProgressTracker: true
    },
    {
      path: 'schedule-appointment',
      progressTitle: 'We Are Here To Serve You',
      component: <Steps.Appointment
        formState={formState.pages.appointment}
        quoter={formState.pages.quoter}
        basicInfo={basicInfo}
        onFieldChange={(field, value) => onFieldChange('appointment', field, value)}
        onNext={onNext}
        onBack={onBack}
        product={suppData.name}
        journeyType={JourneyTypes.APP_REQUEST}
        isAppointmentSchedulingEnabled={true}
        isDropTicket={true}
        baseURL={'/quote/supplemental/schedule-appointment'}
        setMultiField={(firstName,lastName,email)=>{setFormState({ ...formState, pages: { ...formState.pages, appointment: { ...formState.pages.appointment, firstName: firstName, lastName: lastName, email: email } } })
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(formState));
      }}    
      fullFormState={formState}
      onMultiFieldChange={(fs) => {setFormState(fs)}}     
      />,
      hideProgressTracker: true,
      hideMainHeader: true,
      headerText: 'We Are Here To Serve You',
      whiteBackground: true
    },
    {
      path: 'thank-you',
      progressTitle: 'Thank You',
      headerText:"Verifying Your Information",
      component: <Steps.ThankYou 
        sessionStorageKey={SESSION_STORAGE_KEY} 
        name={formState.pages.appointment.firstName}
        date={formState.pages.appointment.specificApptTime}
        basicInfo={basicInfo}
       />,
      hideProgressTracker: true,
    }
  ];

  const currentStepObj = steps[formState.currentStep];

  return (
    <div>
      <Helmet>
        <title>{`Supplemental insurance  - ${currentStepObj.progressTitle}`}</title>
      </Helmet>

      <HeaderStepper
        title={currentStepObj.headerText}
        hidden={props.landing || currentStepObj.hideMainHeader}
        hideProgressTracker={currentStepObj.hideProgressTracker}
        stepName={currentStepObj.progressTitle}
        step={formState.currentStep + 1}
        totalSteps={steps.length}
        whiteBg={currentStepObj.whiteBackground}
      >
        {currentStepObj.component}
      </HeaderStepper>
    </div>
  );
});

Supplemental.propTypes = {
  landing: PropTypes.bool,
};

export default Supplemental;
